<template lang="pug">
.FDownloadForm
  FCard.FDownloadForm__content(
    :color="color"
    :text-color="textColor"
  )
    h3(v-if="title") {{ title }}
    FForm.FDownloadForm__form(
      v-slot="{ submit }"
      :initial-values="formInitialValues"
      @valid="handleValidForm"
    )
      FGrid(
        columns="12"
        gap="16"
      )
        FGridItem(span="12")
          FInput(
            name="email"
            :placeholder="t('download_form__label__email')"
            :error-message="t('forms__error_feedback__email')"
            :rules="[required, email]"
            error-color="danger"
          )
        FGridItem(
          :span="6"
          sm="12"
        )
          FInput(
            name="firstname"
            :placeholder="t('download_form__label__first_name')"
            :error-message="t('forms__error_feedback__required')"
            :rules="required"
            error-color="danger"
          )
        FGridItem(
          :span="6"
          sm="12"
        )
          FInput(
            name="lastname"
            :placeholder="t('download_form__label__last_name')"
            :error-message="t('forms__error_feedback__required')"
            :rules="required"
            error-color="danger"
          )
        FGridItem(span="12")
          FInput(
            name="company"
            :placeholder="t('download_form__label__company')"
            :error-message="t('forms__error_feedback__required')"
            :rules="required"
            error-color="danger"
          )
        FGridItem(span="12")
          FCheckbox(
            name="marketingConsent"
            :label="t('forms__marketing_consent')"
            :text-color="textColor"
            hide-hint
          )
        FGridItem(span="12")
          FCheckbox(
            name="storeAndProcessConsent"
            :label="`${t('forms__store_process_consent')}*`"
            :rules="required"
            :text-color="textColor"
            error-color="danger"
            :error-message="t('forms__error_feedback__process_consent')"
          )
      FGridItem.FDownloadForm__submitButton(
        v-if="ctaText"
        :span="12"
      )
        FButton(
          :size="smAndDown('large', 'giant')"
          :color="ctaColor"
          :loading="loading"
          @click="submit"
        ) {{ ctaText }}

      FGridItem(:span="12")
        .Form__message--success(
          v-if="success"
          v-html="t('download_form__success_message')"
        )
        .Form__message--error(
          v-else-if="error"
          v-html="t('download_form__error_message')"
        )
</template>

<style lang="stylus">
.FDownloadForm
  max-width rem(1088)

.FDownloadForm__form
  width 100%

.FDownloadForm__content
  display flex
  flex-direction column
  align-items center
  padding rem(48)

  h3
    text-align center
    margin-bottom rem(32)

  +media-down('sm')
    padding rem(32)

.FDownloadForm__submitButton
  display flex
  justify-content flex-start
  margin-top rem(24)

  +media-down('sm')
    justify-content center
</style>

<script setup lang="ts">
import { email, required } from '@vee-validate/rules';

export interface FDownloadFormProps {
  /**
   * Title of the form
   */
  title?: string;
  /**
   * The hubspot form (required to submit the form)
   */
  formId?: string;
  /**
   * The PDF URL to be open in a new tag when form is submitted sucessfully
   */
  pdfUrl?: string;
  /**
   * Background color of the form
   */
  color?: Color;
  /**
   * Text color of the form
   */
  textColor?: Color;
  /**
   * CTA text of the form
   */
  ctaText?: string;
  /**
   * CTA background color of the form
   */
  ctaColor?: Color;
}

const props = withDefaults(defineProps<FDownloadFormProps>(), {
  title: '',
  formId: '',
  pdfUrl: '',
  color: 'secondary',
  textColor: 'neutral--light-5',
  ctaText: '',
  ctaColor: 'info',
});

const { t } = useI18n();
const formInitialValues: Record<string, unknown> = {
  firstname: '',
  lastname: '',
  email: '',
  company: '',
  storeAndProcessConsent: false,
  marketingConsent: false,
};

const config = useRuntimeConfig();
const { success, error, loading, submitForm } = useHubspotForm();

const { smAndDown } = useFBreakpoints();

async function handleValidForm(
  values: typeof formInitialValues
): Promise<void> {
  await submitForm(props.formId, values, {
    storeAndProcess: {
      field: 'storeAndProcessConsent',
      text: t('forms__store_process_consent'),
    },
    marketing: [
      {
        field: 'marketingConsent',
        text: t('forms__marketing_consent'),
        id: config.public.hubspotMarketingSubscriptionId,
      },
    ],
  });
  if (success.value && props.pdfUrl) {
    window.open(props.pdfUrl, '_blank');
  }
}
</script>
